/* eslint-disable react-hooks/rules-of-hooks */
import React from 'react'
import styled, { StyledComponent } from '@emotion/styled'
import { Colors, Shapes, Typographies, useTypography } from 'src/components/theme'
import { Autocomplete, AutocompleteRenderOptionState, css } from '@mui/material'
import Popper from '@mui/material/Popper'
import { TextField } from 'src/components/text-field'
import { KeyboardArrowDown } from '@emotion-icons/material/KeyboardArrowDown'
import { FANCY_SCROLLBAR } from 'src/utils/styles'
import { Theme } from '@emotion/react'

type SearchFieldContentProps = {
  fullWidth?: boolean
}

const SearchFieldContent = styled.div`
  width: ${(props: SearchFieldContentProps) => (props.fullWidth ? '100%' : `${Shapes.dropdownWidth}px`)};

  & .MuiAutocomplete-inputRoot {
    padding: 0 !important;
  }

  & input {
    caret-color: transparent;
  }
`

const NormalStyledTextField = styled(TextField)`
  & input {
    padding-left: ${Shapes.spacing[3]}px !important;
    cursor: pointer;
  }
`
const PlainStyledTextField = styled(TextField)`
  background-color: transparent;

  & > div {
    background-color: transparent !important;
  }

  & > div > div {
    background-color: transparent !important;
  }

  & > div > input {
    background: none;
    padding-left: ${Shapes.spacing[3]}px !important;
    caret-color: transparent;
  }

  & > div > fieldset {
    border: none;
    box-shadow: none !important;
  }

  & > div > div:hover > fieldset {
    box-shadow: none !important;
  }
`

const PopOver = styled(Popper)`
  background: none !important;
`

const Paper = styled.div`
  background: none !important;
`

const Listbox = styled.div`
  padding: 0 !important;
  margin: 0 !important;
  background-color: ${Colors.bgWhite};
  border: 1px solid ${Colors.borderInputHover};
  overflow-x: hidden;
  overflow-y: scroll !important;
  overflow-y: overlay !important;
  ${FANCY_SCROLLBAR}
  border-radius: ${Shapes.borderRadius}px;

  & li[data-focus='true'] {
    background-color: ${Colors.bgMedium};
    & svg {
      fill: ${Colors.blueDark};
    }
  }

  & li {
    padding: ${Shapes.spacing[2]}px;
    padding-left: ${Shapes.spacing[3]}px;
    border-bottom: 1px solid ${Colors.borderInput};
    &:hover {
      background-color: ${Colors.bgLight};
    }
    &[aria-selected='true'] {
      background-color: ${Colors.bgMedium};
    }
    &:last-of-type {
      border-bottom: none;
    }
  }
`

const ListboxRounded = styled(Listbox)`
  border-radius: ${Shapes.borderRadius}px;
  border-top-width: 1px;
`

const OptionAction = styled.div`
  display: none;
  gap: ${Shapes.spacing[1]}px;
  color: ${Colors.blueDark};
`

type ListItemProps = {
  persistentAction?: boolean
}
const ListItem = styled.li<ListItemProps>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${Shapes.spacing[1]}px ${Shapes.spacing[3]}px;
  cursor: pointer;
  ${useTypography(Typographies.text1)}

  ${({ persistentAction }) => {
    if (persistentAction) {
      return css`
        & ${OptionAction} {
          display: flex;
        }
      `
    }

    return css`
      &:hover ${OptionAction} {
        display: flex;
      }
    `
  }}

  &.active {
    ${useTypography(Typographies.text1)}
    color: ${Colors.textBlack};
  }
` as StyledComponent<
  {
    theme?: Theme | undefined
    as?: React.ElementType<any> | undefined
  } & React.ClassAttributes<HTMLLIElement> &
    React.HTMLAttributes<HTMLLIElement>,
  any,
  any
>

export type DropdownOption = {
  id: string
  label: string
  showAction?: boolean
  icon?: React.ReactNode
  [index: string]: any
}

export type DropdownOptionAction = {
  icon: JSX.Element
  label?: string
  onClick?: (option: DropdownOption | null) => void
}

type DropdownVariant = 'normal' | 'plain'

export type DropdownProps = {
  name?: string
  options: DropdownOption[]
  optionsAction?: DropdownOptionAction
  value?: string | null
  variant?: DropdownVariant
  onChange?: (newVal: string | null) => void
  onChangeOption?: (newVal: DropdownOption | null) => void
  placeholder?: string
  isInitiallyOpen?: boolean
  fullWidth?: boolean
  label?: string
  style?: React.CSSProperties
  className?: string
  disabled?: boolean
  OptionComponent?: (props: any, id: string) => React.ReactElement
  onBlur?: React.FocusEventHandler<HTMLDivElement>
  error?: boolean
  helperText?: string
  renderOption?: (params: {
    option: DropdownOption
    state: AutocompleteRenderOptionState
    props: React.HTMLAttributes<HTMLLIElement>
  }) => React.ReactNode | undefined
  persistentAction?: boolean
}

const emptyOptions: DropdownOption[] = []

export const Dropdown: React.FC<DropdownProps> = (props) => {
  const {
    options = emptyOptions,
    optionsAction,
    variant = 'normal',
    value,
    placeholder,
    label,
    onChange,
    onChangeOption,
    isInitiallyOpen,
    fullWidth,
    style,
    className,
    disabled,
    OptionComponent,
    onBlur,
    error,
    helperText,
    renderOption,
    persistentAction,
  } = props
  const [open, setOpen] = React.useState(isInitiallyOpen ?? false)
  const internalOptions = options.map((x) => x.id)

  const getLabel = (id: string | null | undefined) => {
    if (!id) {
      return ''
    }
    return options.find((x) => x.id === id)?.label || ''
  }

  const onAutoCompleteChange = (_event: any, newValue: string | null) => {
    if (onChange) {
      onChange(newValue)
    }
    if (onChangeOption) {
      if (newValue === null) {
        onChangeOption(null)
      }
      onChangeOption(options.find((x) => x.id === newValue) || null)
    }
  }

  const handleRenderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    optionId: string,
    state: AutocompleteRenderOptionState
  ) => {
    const option = options.find((o) => o.id === optionId)
    if (!option || !renderOption) {
      return
    }
    return renderOption({ props, option, state })
  }

  const ListBoxComponent = variant === 'plain' ? ListboxRounded : Listbox

  return (
    <SearchFieldContent fullWidth={fullWidth} className={className} style={style}>
      <Autocomplete
        disabled={disabled}
        filterOptions={(x) => x}
        options={internalOptions}
        autoComplete={false}
        clearIcon={false}
        disableClearable
        includeInputInList
        popupIcon={!disabled && <KeyboardArrowDown size={22} color={Colors.blueDark} />}
        value={value == null ? undefined : value}
        inputValue={getLabel(value)}
        selectOnFocus={false}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        PopperComponent={PopOver}
        PaperComponent={Paper}
        ListboxComponent={ListBoxComponent}
        onChange={onAutoCompleteChange}
        onBlur={onBlur}
        renderInput={(params) => {
          switch (variant) {
            case 'plain':
              return (
                <PlainStyledTextField
                  {...params}
                  className={open ? 'expanded' : ''}
                  placeholder={placeholder}
                  label={label}
                  error={error}
                  helperText={helperText}
                />
              )
            case 'normal':
              return (
                <NormalStyledTextField
                  {...params}
                  className={open ? 'expanded' : ''}
                  placeholder={placeholder}
                  label={label}
                  error={error}
                  helperText={helperText}
                />
              )
          }
        }}
        renderOption={(props, id: string, state) => {
          const option = options.find((x) => x.id === id)
          return !!renderOption ? (
            handleRenderOption(props, id, state)
          ) : (
            <ListItem {...props} persistentAction={persistentAction} className={value === id ? 'active' : ''}>
              {OptionComponent ? OptionComponent(props, id) : getLabel(id)}
              {optionsAction && option?.showAction && (
                <OptionAction
                  onClick={(e) => {
                    e.stopPropagation()
                    optionsAction.onClick?.(option || null)
                    setOpen(false)
                  }}>
                  {optionsAction.icon}
                  {optionsAction.label}
                </OptionAction>
              )}
            </ListItem>
          )
        }}
      />
    </SearchFieldContent>
  )
}
